import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, NgZone } from '@angular/core';
import { FormGroup, AbstractControl, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Company } from '../../../class/company';
import { CompanyService } from '../../../service/company/company.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem, MultiSelectModule, AutoCompleteModule, CalendarModule } from 'primeng/primeng';
import { MapsAPILoader } from '@agm/core';
import { Marker } from '../../../../../interfaces/marker';
import { NotificationsService } from '../../../../../theme/services/notifications/notifications.service';
import { formatDateWithoutHour } from '../../../../../theme/functions/formatDateWithoutHour.function';
import { calendarES } from '../../../../../theme/functions/calendarES';

@Component({
  selector: 'app-new-company',
  templateUrl: './new-company.component.html',
  styleUrls: ['./new-company.component.scss']
})
export class NewCompanyComponent implements OnInit {
    @Output() msg: EventEmitter<object> = new EventEmitter<object>();
    @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() edit: Company;
    public promiseForm: Promise<any>;
    public formCompany: FormGroup;
    public identification_number: AbstractControl;
    public name: AbstractControl;
    public latitude: number;
    public longitude: number;
    public economic_activity: AbstractControl;
    public legal_representative_identification_number: AbstractControl;
    public legal_representative_name: AbstractControl;
    public legal_representative_employee_position: AbstractControl;
    public director_rrhh: AbstractControl;
    public city: AbstractControl;
    public phone: AbstractControl;
    public web: AbstractControl;
    public inscription_cc_at: AbstractControl;
    public addressGoogle: string;
    public overlays: any[];
    public selectedPosition: any;
    public address: AbstractControl;
    public activated: AbstractControl;

    @ViewChild('search') public searchElementRef: ElementRef;
    public zoom: number;
    public marker: Marker;

    public es: any;

    constructor(
        fb: FormBuilder,
        public companyService: CompanyService,
        public translate: TranslateService,
        private ngZone: NgZone,
        private notificationsService: NotificationsService,
        private mapsAPILoader: MapsAPILoader) {

        this.es = calendarES();

        this.formCompany = fb.group({
            'identification_number': ['', Validators.compose([Validators.required, Validators.minLength(2)])],
            'name': ['', Validators.compose([Validators.required, Validators.minLength(2)])],
            'address': ['', Validators.compose([Validators.required, Validators.minLength(1)])],
            'economic_activity': ['', Validators.compose([Validators.required])],
            'legal_representative_identification_number': ['', Validators.compose([Validators.required])],
            'legal_representative_name': ['', Validators.compose([Validators.required])],
            'legal_representative_employee_position': ['', Validators.compose([Validators.required])],
            'director_rrhh': ['', Validators.compose([Validators.required])],
            'city': ['', Validators.compose([Validators.required])],
            'phone': ['', Validators.compose([Validators.required])],
            'web': ['', Validators.compose([Validators.required])],
            'inscription_cc_at': ['', Validators.compose([Validators.required])],
            'activated': ['', Validators.compose([Validators.required])],
        });

        this.identification_number = this.formCompany.controls['identification_number'];
        this.name = this.formCompany.controls['name'];
        this.address = this.formCompany.controls['address'];
        this.economic_activity = this.formCompany.controls['economic_activity'];
        this.legal_representative_identification_number = this.formCompany.controls['legal_representative_identification_number'];
        this.legal_representative_name = this.formCompany.controls['legal_representative_name'];
        this.legal_representative_employee_position = this.formCompany.controls['legal_representative_employee_position'];
        this.director_rrhh = this.formCompany.controls['director_rrhh'];
        this.city = this.formCompany.controls['city'];
        this.phone = this.formCompany.controls['phone'];
        this.web = this.formCompany.controls['web'];
        this.inscription_cc_at = this.formCompany.controls['inscription_cc_at'];
        this.activated = this.formCompany.controls['activated'];

        // set google maps defaults
        this.zoom = 2;
        this.latitude = 39.8282;
        this.longitude = -98.5795;

        this.marker = {
            lat: this.latitude,
            lng: this.longitude,
            label: 'Company',
            draggable: true
        };
    }

    ngOnInit() {
        if (this.edit) {
            this.identification_number.setValue(this.edit.identification_number);
            this.name.setValue(this.edit.name);
            this.economic_activity.setValue(this.edit.economic_activity);
            this.legal_representative_identification_number.setValue(this.edit.legal_representative_identification_number);
            this.legal_representative_name.setValue(this.edit.legal_representative_name);
            this.legal_representative_employee_position.setValue(this.edit.legal_representative_employee_position);
            this.director_rrhh.setValue(this.edit.director_rrhh);
            this.city.setValue(this.edit.city);
            this.phone.setValue(this.edit.phone);
            this.city.setValue(this.edit.city);
            this.web.setValue(this.edit.web);
            this.inscription_cc_at.setValue(new Date(this.edit.inscription_cc_at));
            this.activated.setValue(this.edit.activated);
            this.address.setValue(this.edit.address);
            if (this.edit.latitude && this.edit.longitude) {
                const lat = this.edit.latitude;
                const lng = this.edit.longitude;
                const position = {
                    lat : lat,
                    lng : lng
                };
                this.setPosition(position);
            }
        } else {
            this.activated.setValue(true);
            this.setCurrentPosition();
        }

        // load Places Autocomplete
        this.mapsAPILoader.load().then(() => {
            const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
                types: ['address']
            });
            autocomplete.addListener('place_changed', () => {
                this.ngZone.run(() => {
                // get the place result
                const place: google.maps.places.PlaceResult = autocomplete.getPlace();

                this.addressGoogle = place.formatted_address;
                // verify result
                if (place.geometry === undefined || place.geometry === null) {
                    return;
                }

                // set latitude, longitude and zoom
                this.latitude = place.geometry.location.lat();
                this.longitude = place.geometry.location.lng();
                this.zoom = 17;
                });
            });
        });
    }

    private setCurrentPosition() {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                this.latitude = position.coords.latitude;
                this.longitude = position.coords.longitude;
                this.zoom = 17;
            });
        }
    }

    private setPosition(position) {
        this.latitude = position.lat;
        this.longitude = position.lng;
        this.zoom = 17;
    }

    public handleMapClick(event) {
        // this.selectedPosition = event.latLng;
        // this.addMarker(this.selectedPosition.lat(), this.selectedPosition.lng());
    }

    public addMarker(lat, lng) {
        const title = (this.name.value !== '') ? this.name.value : 'company';
        this.longitude = lng;
        this.latitude = lat;
        this.overlays = [];
        this.overlays.push(
            new google.maps.Marker({
                position: {
                    lat: lat,
                    lng: lng
                }, title: title}
            )
        );
    }

    public cancelCompany () {
        console.log('reset');
        this.formCompany.reset();
        this.close.emit(true);
    }

    public newCompany(values: Company) {
        const param = {
            'identification_number': values.identification_number,
            'name': values.name,
            'economic_activity': values.economic_activity,
            'legal_representative_identification_number': values.legal_representative_identification_number,
            'legal_representative_name': values.legal_representative_name,
            'legal_representative_employee_position': values.legal_representative_employee_position,
            'director_rrhh': values.director_rrhh,
            'city': values.city,
            'phone': values.phone,
            'web': values.web,
            'inscription_cc_at': formatDateWithoutHour(values.inscription_cc_at),
            'address': this.addressGoogle,
            'latitude': this.latitude,
            'longitude': this.longitude,
            'activated': values.activated
        };
        if (values && this.formCompany.valid) {
            const promise = (this.edit) ?
                this.companyService.updateCompany(this.edit.id, param)
                :
                this.companyService.newCompany(param);

            this.promiseForm = promise.toPromise().then(
                response => {
                    this.translate.get('saved').subscribe((res: string) => {
                        this.notificationsService.growlNotify('success', res);
                        // this.msg.emit( { severity: 'success', summary: res  } );
                        this.close.emit(true);
                        this.formCompany.reset();
                        this.selectedPosition = null;
                    });
                },
                error => {
                    let msg = '';
                    this.translate.get(JSON.parse(error._body).errors[0].detail).subscribe((res1: string) => {
                        this.translate.get(JSON.parse(error._body).errors[1].detail).subscribe((res2: string) => {
                            JSON.parse(error._body).errors.forEach(element => {
                                if (element.source) {
                                    if (element.source.pointer) {
                                        this.translate.get(element.source.pointer).subscribe((res3: string) => {
                                            msg = msg + res3 + ', ';
                                        });
                                    }
                                }
                            });
                            this.notificationsService.growlNotify('error', res1 + ' ' + res2 + ': ' + msg);
                            // this.msg.emit( { severity: 'error', summary: 'Error' } );
                        });
                    });
                }
            );
        }
    }

    markerDragEnd(m: Marker, $event: any) {
        this.latitude = $event.coords.lat;
        this.longitude = $event.coords.lng;
    }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/employee/pages/companies/new-company/new-company.component.ts